import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import {
  CalendarDays, User, CreditCard, MapPin, Bell, BellOff,
  HelpCircle, Mail, AlertTriangle, FileCheck, FileText, Eye,
  Sparkles, Sun, Accessibility,
  Shield, LogOut, Loader2, ArrowRight, ChevronRight, Clock, Plus,
  CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/shared/context/AuthContext";
import { api, formatApiError } from "@/shared/lib/api";
import { formatTime12 } from "@/shared/lib/format";
import Skeleton from "@/shared/motion/Skeleton";
import { AccountPrompts } from "@/apps/rider/components/AccountPrompts";
import AccountDashboard from "@/apps/rider/components/AccountDashboard";

/**
 * /account — Rider commuter dashboard.
 *
 * Surfaces operational state at a glance:
 *   - Time-aware greeting + dynamic context line (next pickup, ride count, etc.)
 *   - Hero "Next Ride" card with countdown to pickup window (or empty CTA)
 *   - Access/membership status (Pay-per-ride or Link Commute Plan)
 *   - Payment health (default card or attention banner)
 *   - Notifications status (push permission + per-account toggles summary)
 *   - Grouped sections: Quick Actions / Preferences / Support / Legal
 *   - De-emphasized Sign Out tucked at the bottom.
 *
 * All cards are read-only here — deep edits live in the dedicated sub-pages
 * (/payment-methods, /preferences/notifications, /trips, etc.).
 */
export default function Account({ inSheet = false, hideIdentityHeader = false }) {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) {
    // Account-page skeleton: identity strip + next-ride card + a couple
    // of section blocks. Matches the outer shape a returning rider
    // expects so the swap into real content feels like a smooth fade.
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="account-page-loading">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-full" testid="account-skeleton-avatar" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <Skeleton className="h-40 w-full rounded-3xl" testid="account-skeleton-next-ride" />
        <Skeleton className="h-32 w-full rounded-3xl" testid="account-skeleton-payment" />
        <Skeleton className="h-20 w-full rounded-2xl" />
      </div>
    );
  }
  // Signed-out riders land in the cinematic welcome surface, not the
  // legacy /account "welcome card" — keeps the brand entry consistent.
  // Inside the drawer (inSheet), render a compact sign-in / register
  // CTA so the downward-expanding pill always feels purposeful even
  // before the rider has an account.
  if (!user) {
    if (inSheet) {
      return (
        <div data-testid="account-page-logged-out-sheet" className="bg-[#F9F7F4]">
          <div
            className={`max-w-md mx-auto px-4 sm:px-6 pb-[max(env(safe-area-inset-bottom),32px)] space-y-4 ${hideIdentityHeader ? "pt-2" : "pt-3"}`}
          >
            <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#ed6500]">
              Account
            </div>
            <h1 className="font-serif font-light text-3xl sm:text-4xl leading-tight text-[#0A1128]">
              Welcome to Link.
            </h1>
            <p className="text-[13px] text-[#515E7B]">
              A scheduled, recurring transportation plan — pickup and return, every week. Sign in or create your account to book your first route.
            </p>
            <div className="grid gap-2 pt-2">
              <Link
                to="/login"
                className="bg-[#0A1128] text-[#F9F7F4] px-5 py-3.5 inline-flex items-center justify-between rounded-2xl shadow-md"
                data-testid="account-signin-cta"
              >
                <span className="font-semibold tracking-wide text-[13px]">Sign in</span>
                <ArrowRight size={15} className="text-[#ed6500]" />
              </Link>
              <Link
                to="/onboarding"
                className="bg-white border hairline px-5 py-3.5 inline-flex items-center justify-between text-[#0A1128] hover:border-[#ed6500] transition-colors rounded-2xl"
                data-testid="account-register-cta"
              >
                <span className="font-semibold tracking-wide text-[13px]">Create account</span>
                <ArrowRight size={15} className="text-[#ed6500]" />
              </Link>
            </div>
            <p className="text-[11px] text-[#515E7B] pt-1">
              New to Link? <Link to="/about" className="text-[#ed6500] hover:underline">Learn how it works</Link>.
            </p>
          </div>
        </div>
      );
    }
    return <Navigate to="/welcome" replace />;
  }

  const handleLogout = async () => {
    try { await logout(); nav("/account"); }
    catch { toast.error("Could not sign out"); }
  };

  // When mounted INSIDE the AccountSheetRoute drawer we drop the
  // viewport-fixed positioning (which would punch through the sheet
  // and cover the map). The sheet already supplies its own scroll
  // container — we just render plain content here.
  if (inSheet) {
    return (
      <div data-testid="account-page">
        <div
          className="max-w-2xl mx-auto pb-[max(env(safe-area-inset-bottom),32px)]"
        >
          {!hideIdentityHeader && (
            <div className="px-4 pt-3"><IdentityHeader user={user} /></div>
          )}
          <AccountPrompts />
          {/* Feb 2026 · Commute dashboard replaces the settings-menu
              rows. Glass surface, seven rows in fixed order, Settings
              collapses everything else into a nested slide-in. */}
          <AccountDashboard />
          <div className="px-4 pt-2">
            <SignOutLink onLogout={handleLogout} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col" data-testid="account-page">
      <div className="flex-1 min-h-0 overflow-y-auto wheel-scroll">
        <div
          className="max-w-2xl mx-auto pb-[max(env(safe-area-inset-bottom),96px)]"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
        >
        <div className="px-4"><IdentityHeader user={user} /></div>
        <AccountPrompts />
        <AccountDashboard />

        {/* De-emphasized sign-out — small text link, not a primary card. */}
        <div className="px-4">
          <button
            onClick={handleLogout}
            className="mx-auto mt-2 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#96a2c0] hover:text-[#ed6500] transition-colors py-3 px-2"
            data-testid="account-logout"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
   §1 — Identity header
   ===================================================================== */
function SignOutLink({ onLogout }) {
  return (
    <button
      onClick={onLogout}
      className="mx-auto mt-2 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold hover:text-[#ed6500] transition-colors py-3 px-2"
      style={{ color: "var(--dusk-text-muted)" }}
      data-testid="account-logout"
    >
      <LogOut size={12} /> Sign out
    </button>
  );
}

function IdentityHeader({ user }) {
  const [nextPickup, setNextPickup] = useState(undefined); // undefined=loading, null=none
  const [weekCount, setWeekCount] = useState(null);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        // Single round-trip — derive both the next pickup and the week
        // count from /trips/me. Avoids the duplicate /me/trip/upcoming
        // request that NextRideCard already makes (perf nit from iter 58).
        const { data: trips } = await api.get("/trips/me");
        if (stop) return;
        const list = Array.isArray(trips) ? trips : [];
        const todayIso = new Date().toISOString().slice(0, 10);
        const upcoming = list
          .filter((t) => t?.date && t.date >= todayIso && t.status !== "cancelled" && t.status !== "completed")
          .sort((a, b) => {
            if (a.date !== b.date) return a.date.localeCompare(b.date);
            return (a.pickup_time || "").localeCompare(b.pickup_time || "");
          })[0] || null;
        setNextPickup(upcoming);
        // Count rides in the current ISO week (Mon-Sun).
        const now = new Date();
        const day = (now.getDay() + 6) % 7; // Mon=0..Sun=6
        const monday = new Date(now); monday.setDate(now.getDate() - day); monday.setHours(0,0,0,0);
        const sunday = new Date(monday); sunday.setDate(monday.getDate() + 7);
        const inWeek = list.filter((t) => {
          if (!t?.date) return false;
          const d = new Date(t.date);
          return d >= monday && d < sunday && t.status !== "cancelled";
        }).length;
        setWeekCount(inWeek);
      } catch (_) {
        if (!stop) { setNextPickup(null); setWeekCount(0); }
      }
    })();
    return () => { stop = true; };
  }, []);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    const part = h >= 5 && h < 12 ? "Good Morning"
                : h >= 12 && h < 18 ? "Good Afternoon"
                : "Good Evening";
    const first = (() => {
      const explicit = user.firstName || user.first_name;
      if (explicit) return explicit;
      const full = (user.name || "").trim();
      return full ? full.split(/\s+/)[0] : null;
    })();
    return first ? `${part}, ${first}` : part;
  }, [user]);

  // Dynamic secondary line — priority: next pickup → week count → fallback.
  const subline = useMemo(() => {
    if (nextPickup === undefined) return null; // suppress while loading
    if (nextPickup) {
      const dayLabel = formatRelativeDay(nextPickup.date);
      return `Next pickup ${dayLabel} at ${formatTime12(nextPickup.pickup_time || "")}`;
    }
    if (typeof weekCount === "number" && weekCount > 0) {
      return `${weekCount} ride${weekCount === 1 ? "" : "s"} scheduled this week`;
    }
    return "Ready when you are";
  }, [nextPickup, weekCount]);

  return (
    <header className="flex items-center gap-4" data-testid="account-header">
      {user.profile_picture ? (
        <img
          src={user.profile_picture}
          alt=""
          className="w-14 h-14 rounded-full object-cover border hairline shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-full bg-[#0A1128] text-[#F9F7F4] flex items-center justify-center font-serif text-xl shrink-0">
          {(user.name || user.email || "·").slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">
          Link commuter
        </div>
        <div
          className="font-serif text-[22px] sm:text-[26px] leading-tight text-[#F9F7F4] truncate"
          data-testid="account-greeting"
        >
          {greeting}
        </div>
        {subline && (
          <div className="text-[12.5px] text-[#96a2c0] mt-0.5 truncate" data-testid="account-greeting-subline">
            {subline}
          </div>
        )}
      </div>
    </header>
  );
}

/* =====================================================================
   §2 — Next Ride hero card
   ===================================================================== */
function NextRideCard() {
  const nav = useNavigate();
  const [trip, setTrip] = useState(undefined); // undefined=loading
  const [route, setRoute] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get("/me/trip/upcoming");
        if (stop) return;
        setTrip(data?.trip || null);
        setRoute(data?.route || null);
      } catch (_) {
        if (!stop) setTrip(null);
      }
    })();
    return () => { stop = true; };
  }, []);

  // Tick every 60s to keep countdown fresh.
  useEffect(() => {
    if (!trip) return;
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [trip]);

  if (trip === undefined) {
    // Skeleton in the same rounded-3xl / navy chassis as the real
    // next-ride card so the layout stays put while data resolves.
    return (
      <div className="bg-[#0A1128] rounded-3xl p-5 sm:p-6 relative overflow-hidden" data-testid="next-ride-loading">
        <div className="absolute -right-14 -top-14 w-56 h-56 bg-[#ed6500]/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="space-y-3 relative">
          <Skeleton className="h-3 w-16 bg-white/10" testid="next-ride-skeleton-eyebrow" />
          <Skeleton className="h-8 w-40 bg-white/10" testid="next-ride-skeleton-when" />
          <Skeleton className="h-3 w-56 bg-white/10" testid="next-ride-skeleton-address" />
          <Skeleton className="h-3 w-48 bg-white/10" testid="next-ride-skeleton-driver" />
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <section
        className="bg-[#0A1128] text-[#F9F7F4] rounded-3xl p-5 sm:p-6 relative overflow-hidden"
        data-testid="next-ride-empty"
      >
        <div className="absolute -right-14 -top-14 w-56 h-56 bg-[#ed6500]/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">
          Next ride
        </div>
        <div className="font-serif text-[26px] sm:text-[28px] leading-tight mt-2">
          No rides scheduled
        </div>
        <p className="text-[13px] text-[#F9F7F4]/70 mt-2 max-w-sm">
          Book your next commute in seconds — set the route, pick the days, slide to schedule.
        </p>
        <button
          type="button"
          onClick={() => nav("/")}
          className="mt-5 inline-flex items-center gap-2 bg-[#ed6500] hover:bg-[#ff8a36] text-white px-5 py-3 rounded-2xl text-[12px] tracking-[0.2em] uppercase font-semibold transition-colors"
          data-testid="next-ride-cta-schedule"
        >
          <Plus size={14} strokeWidth={2.4} /> Schedule ride
        </button>
      </section>
    );
  }

  const pickupAt = pickupDateTime(trip.date, trip.pickup_time);
  const minutesAway = pickupAt ? Math.max(0, Math.round((pickupAt.getTime() - now) / 60000)) : null;
  const windowStr = formatCountdown(minutesAway);
  const dayLabel = formatRelativeDay(trip.date);
  const fromLabel = (trip.pickup_address || route?.pickup_address || "Pickup").split(",")[0];
  const toLabel = (trip.dropoff_address || route?.destination_address || "Destination").split(",")[0];
  const tagline = trip.direction === "return" ? "Return ride" : "Outbound ride";

  return (
    <section
      className="bg-[#0A1128] text-[#F9F7F4] rounded-3xl p-5 sm:p-6 relative overflow-hidden"
      data-testid="next-ride-card"
    >
      <div className="absolute -right-12 -top-12 w-56 h-56 bg-[#ed6500]/12 rounded-full blur-3xl" aria-hidden="true" />
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">
          Next ride
        </div>
        <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#F9F7F4]/55">
          {tagline}
        </div>
      </div>
      <div className="font-serif text-[24px] sm:text-[27px] leading-tight mt-2 tabular-nums">
        {dayLabel} · {formatTime12(trip.pickup_time || "")}
      </div>
      <div className="flex items-center gap-2 text-[14.5px] mt-2 min-w-0">
        <span className="w-1.5 h-1.5 rounded-full bg-[#5c97f7] shrink-0" />
        <span className="truncate">{fromLabel}</span>
        <ArrowRight size={13} className="text-[#ed6500] shrink-0" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#ed6500] shrink-0" />
        <span className="truncate">{toLabel}</span>
      </div>
      {windowStr && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] tracking-wide text-[#F9F7F4]/75" data-testid="next-ride-countdown">
          <Clock size={12} className="text-[#ed6500]" />
          Pickup window opens in {windowStr}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <Link
          to="/trips"
          className="inline-flex items-center gap-2 bg-[#ed6500] hover:bg-[#ff8a36] text-white px-4 py-2.5 rounded-xl text-[11px] tracking-[0.2em] uppercase font-semibold transition-colors"
          data-testid="next-ride-view"
        >
          View ride <ChevronRight size={12} strokeWidth={2.4} />
        </Link>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-[#F9F7F4] px-4 py-2.5 rounded-xl text-[11px] tracking-[0.2em] uppercase font-semibold border border-white/10 transition-colors"
          data-testid="next-ride-modify"
        >
          Modify schedule
        </Link>
      </div>
    </section>
  );
}

/* =====================================================================
   §3 — Access / membership status
   ===================================================================== */
function AccessStatusCard() {
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data: res } = await api.get("/me/subscription");
        if (!stop) setData(res || null);
      } catch (_) {
        if (!stop) setData(null);
      }
    })();
    return () => { stop = true; };
  }, []);

  if (data === undefined) return <SkeletonCard testId="access-card-loading" />;

  const sub = data?.subscription || null;
  const route = data?.route || null;

  if (!sub) {
    return (
      <StatusCard
        eyebrow="Current access"
        title="Pay-per-ride"
        subtitle="No active subscription · Charged per booking"
        Icon={Sparkles}
        testId="access-card"
      />
    );
  }

  const days = Array.isArray(route?.days) ? route.days.length : (sub.days?.length || 0);
  const renewalLabel = sub.next_renewal
    ? `Next renewal ${formatLongDate(sub.next_renewal)}`
    : "Next renewal pending";
  const planName = sub.billing_cycle === "monthly" ? "Monthly plan"
                 : sub.billing_cycle === "biweekly" ? "Bi-weekly plan"
                 : "Weekly plan";

  return (
    <StatusCard
      eyebrow={`Link commute · ${planName}`}
      title={`${days} ${days === 1 ? "day" : "days"} selected`}
      subtitle={renewalLabel}
      Icon={Sparkles}
      attention={sub.needs_confirmation}
      attentionLabel={sub.needs_confirmation ? "Action needed" : null}
      testId="access-card"
    />
  );
}

/* =====================================================================
   §4 — Payment health
   ===================================================================== */
function PaymentStatusCard() {
  const [data, setData] = useState(undefined);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data: res } = await api.get("/me/payment-methods");
        if (!stop) setData(res);
      } catch (_) {
        if (!stop) setData({ methods: [], has_customer: false });
      }
    })();
    return () => { stop = true; };
  }, []);

  if (data === undefined) return <SkeletonCard testId="payment-status-loading" />;

  const methods = data?.methods || [];
  const def = methods.find((m) => m.is_default) || methods[0];

  // Three states: no card on file, expired/expiring soon, healthy default.
  let title, subtitle, attention = false;
  if (!def) {
    title = "Payment method requires attention";
    subtitle = "Add a card to schedule rides without interruption";
    attention = true;
  } else {
    const now = new Date();
    const exp = new Date(def.exp_year, (def.exp_month || 1) - 1, 1);
    const monthsToExpiry = (exp.getFullYear() - now.getFullYear()) * 12 + (exp.getMonth() - now.getMonth());
    if (monthsToExpiry < 0) {
      title = "Card expired";
      subtitle = `${brandLabel(def.brand)} ending in ${def.last4} · Update to keep rides scheduled`;
      attention = true;
    } else if (monthsToExpiry <= 1) {
      title = `${brandLabel(def.brand)} ending in ${def.last4}`;
      subtitle = `Expires ${String(def.exp_month).padStart(2, "0")}/${String(def.exp_year).slice(-2)} · Replace soon`;
      attention = true;
    } else {
      title = `${brandLabel(def.brand)} ending in ${def.last4}`;
      subtitle = "Default payment method active";
    }
  }

  return (
    <StatusCard
      eyebrow="Payment status"
      title={title}
      subtitle={subtitle}
      Icon={CreditCard}
      to="/payment-methods"
      attention={attention}
      attentionLabel={attention ? "Needs attention" : null}
      testId="payment-status-card"
    />
  );
}

/* =====================================================================
   §5 — Notifications status
   ===================================================================== */
function NotificationsStatusCard() {
  const [permission, setPermission] = useState(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    return Notification.permission;
  });
  const [prefs, setPrefs] = useState(undefined);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get("/me/notification-prefs");
        if (!stop) setPrefs(data);
      } catch (_) {
        if (!stop) setPrefs(null);
      }
    })();
    return () => { stop = true; };
  }, []);

  // Re-read permission when tab gains focus (user returns from settings).
  useEffect(() => {
    const onFocus = () => {
      if (typeof window !== "undefined" && "Notification" in window) {
        setPermission(Notification.permission);
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  let title, subtitle, attention = false, Icon = Bell;
  if (permission === "unsupported") {
    title = "Notifications unavailable";
    subtitle = "This device or browser doesn't support push notifications";
    Icon = BellOff;
  } else if (permission === "denied") {
    title = "Notifications disabled";
    subtitle = "Enable to receive pickup alerts and ride reminders";
    Icon = BellOff;
    attention = true;
  } else if (permission === "default") {
    title = "Notifications off";
    subtitle = "Enable to never miss a ride window";
    Icon = BellOff;
    attention = true;
  } else {
    // granted
    const activeCount = prefs ? Object.values(prefs).filter(Boolean).length : null;
    title = "Push notifications enabled";
    subtitle = activeCount === null
      ? "Ride reminders active"
      : activeCount === 4
        ? "All ride alerts active"
        : `${activeCount} of 4 alert categories active`;
  }

  return (
    <StatusCard
      eyebrow="Alerts"
      title={title}
      subtitle={subtitle}
      Icon={Icon}
      to="/preferences/notifications"
      attention={attention}
      testId="notifications-status-card"
    />
  );
}

/* =====================================================================
   §6 — Grouped action sections
   ===================================================================== */
function QuickActions({ isOpsAdmin }) {
  return (
    <SectionGroup title="Quick actions" testId="account-section-quick">
      <ActionRow to="/trips"           Icon={CalendarDays} label="Trips"            hint="Upcoming + history" testId="account-link-trips" />
      <ActionRow to="/payment-methods" Icon={CreditCard}   label="Payment methods" hint="Manage cards"        testId="account-link-billing" />
      <ActionRow to="/saved-places"    Icon={MapPin}       label="Saved places"    hint="Home, work, more"   testId="account-link-saved-places" />
      <ActionRow to="/preferences/notifications" Icon={Bell} label="Notifications" hint="Ride alerts + receipts" testId="account-link-notifications" />
      {isOpsAdmin && (
        <ActionRow to="/admin/home" Icon={Shield} label="Admin" hint="Operations control" testId="account-link-admin" />
      )}
    </SectionGroup>
  );
}

function Preferences() {
  return (
    <SectionGroup title="Preferences" testId="account-section-prefs">
      <ActionRow to="/preferences/accessibility" Icon={Accessibility} label="Accessibility" hint="Motion, type size" testId="account-link-accessibility" />
      <ActionRow to="/preferences/appearance"    Icon={Sun}           label="Appearance"    hint="Light, dark, system" testId="account-link-appearance" />
      <ActionRow to="/profile"                   Icon={User}          label="Profile"       hint="Name, photo, contact" testId="account-link-profile" />
    </SectionGroup>
  );
}

function Support() {
  return (
    <SectionGroup title="Support" testId="account-section-support">
      <ActionRow to="/help"            Icon={HelpCircle}    label="Help center"     hint="FAQs + how-tos"      testId="account-link-help" />
      <ActionRow to="/support/contact" Icon={Mail}          label="Contact support" hint="Email our team"      testId="account-link-contact" />
      <ActionRow to="/support/report"  Icon={AlertTriangle} label="Report issue"    hint="Ride or payment"     testId="account-link-report" />
    </SectionGroup>
  );
}

function Legal() {
  return (
    <SectionGroup title="Legal" testId="account-section-legal">
      <ActionRow to="/legal/privacy"       Icon={FileCheck} label="Privacy policy"           testId="account-link-privacy" />
      <ActionRow to="/legal/terms"         Icon={FileText}  label="Terms of service"         testId="account-link-terms" />
      <ActionRow to="/legal/accessibility" Icon={Eye}       label="Accessibility statement" testId="account-link-accessibility-statement" />
    </SectionGroup>
  );
}

/* =====================================================================
   Building blocks
   ===================================================================== */

function SectionGroup({ title, testId, children }) {
  return (
    <section data-testid={testId}>
      <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#515E7B] px-1 mb-2">
        {title}
      </div>
      <div className="bg-white border hairline rounded-2xl divide-y divide-[#E5E1D8] overflow-hidden">
        {children}
      </div>
    </section>
  );
}

function ActionRow({ to, onClick, Icon, label, hint, disabled, extra, testId }) {
  const content = (
    <>
      <span className="w-9 h-9 inline-flex items-center justify-center bg-[#FCEADE] text-[#ed6500] shrink-0 rounded-xl">
        <Icon size={15} strokeWidth={1.9} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#0A1128] text-[14.5px] leading-tight">{label}</div>
        {hint && <div className="text-[11.5px] text-[#515E7B] mt-0.5 truncate">{hint}</div>}
      </div>
      {extra ?? <ChevronRight size={14} className="text-[#515E7B]/70" />}
    </>
  );
  const className = `w-full px-4 py-3.5 flex items-center gap-3 hover:bg-[#F9F7F4]/60 transition-colors ${
    disabled ? "opacity-60 cursor-not-allowed" : ""
  }`;
  if (to) {
    return (
      <Link to={to} className={className} data-testid={testId}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${className} text-left`} data-testid={testId}>
      {content}
    </button>
  );
}

function StatusCard({ eyebrow, title, subtitle, Icon, to, attention = false, attentionLabel = null, testId }) {
  const tone = attention
    ? "bg-[#FCEADE] border-[#ed6500]/30"
    : "bg-white border-[#E5E1D8]";
  const iconTone = attention
    ? "bg-[#ed6500] text-white"
    : "bg-[#FCEADE] text-[#ed6500]";
  const Wrap = to ? Link : "div";
  const wrapProps = to ? { to } : {};
  return (
    <Wrap
      {...wrapProps}
      className={`block rounded-2xl border ${tone} p-4 transition-colors ${to ? "hover:border-[#ed6500]/40" : ""}`}
      data-testid={testId}
      data-attention={attention ? "1" : "0"}
    >
      <div className="flex items-start gap-3">
        <span className={`w-10 h-10 inline-flex items-center justify-center rounded-xl shrink-0 ${iconTone}`}>
          <Icon size={16} strokeWidth={1.9} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">
              {eyebrow}
            </span>
            {attention && attentionLabel && (
              <span className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.18em] uppercase font-semibold text-[#ed6500]">
                <AlertCircle size={10} /> {attentionLabel}
              </span>
            )}
            {!attention && to === "/payment-methods" && (
              <span className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.18em] uppercase font-semibold text-[#0A1128]/55">
                <CheckCircle2 size={10} className="text-[#0A1128]/55" /> Healthy
              </span>
            )}
          </div>
          <div className="font-semibold text-[#0A1128] text-[15px] mt-0.5 leading-snug">
            {title}
          </div>
          {subtitle && (
            <div className="text-[12px] text-[#515E7B] mt-0.5 leading-snug">{subtitle}</div>
          )}
        </div>
        {to && <ChevronRight size={14} className="text-[#515E7B]/60 mt-1 shrink-0" />}
      </div>
    </Wrap>
  );
}

function SkeletonCard({ testId }) {
  return (
    <div className="bg-white border hairline rounded-2xl p-4 min-h-[80px] flex items-center justify-center" data-testid={testId}>
      <Loader2 size={14} className="animate-spin text-[#515E7B]" />
    </div>
  );
}

/* ---------- helpers ---------- */
function brandLabel(brand) {
  const map = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay" };
  if (!brand) return "Card";
  return map[brand.toLowerCase()] || brand.charAt(0).toUpperCase() + brand.slice(1);
}

function formatRelativeDay(dateIso) {
  if (!dateIso) return "";
  try {
    const [y, m, d] = dateIso.split("-").map(Number);
    const trip = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    // Always append the explicit date after "Today" / "Tomorrow" so the
    // rider never has to hunt for the actual calendar day (Phase A tail #2).
    const explicit = trip.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    if (sameDay(trip, today)) return `Today · ${explicit}`;
    if (sameDay(trip, tomorrow)) return `Tomorrow · ${explicit}`;
    return explicit;
  } catch { return dateIso; }
}
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

function formatLongDate(iso) {
  try {
    const d = new Date(iso);
    // Include weekday + year on billing/receipts contexts. Previously
    // rendered "Jul 22" which is ambiguous across years — a rider
    // forwarding to HR could be off by 12 months. (Phase A tail #2)
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function pickupDateTime(dateIso, hhmm) {
  if (!dateIso || !hhmm) return null;
  try {
    const [y, m, d] = dateIso.split("-").map(Number);
    const [hh, mm] = hhmm.split(":").map(Number);
    return new Date(y, m - 1, d, hh, mm);
  } catch { return null; }
}

function formatCountdown(minutes) {
  if (minutes == null || !isFinite(minutes)) return null;
  if (minutes <= 0) return "now";
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const days = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${days}d ${rh}h` : `${days}d`;
}

/* =====================================================================
   Logged-out view (preserved from previous Account page)
   ===================================================================== */
function LoggedOutView() {
  return (
    <div className="fixed inset-0 flex flex-col bg-[#F9F7F4]" data-testid="account-page-logged-out">
      <div className="flex-1 min-h-0 overflow-y-auto wheel-scroll">
        <div
          className="max-w-md mx-auto px-4 sm:px-6 pb-[max(env(safe-area-inset-bottom),96px)]"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 96px)" }}
        >
        <div className="label-xs text-[#ed6500] mb-2">Account</div>
        <h1 className="font-serif font-light text-4xl sm:text-5xl leading-tight text-[#0A1128]">
          Welcome to Link.
        </h1>
        <p className="text-sm text-[#515E7B] mt-3">
          A scheduled, recurring transportation plan — pickup and return, every week. Sign in or create your account to book your first route.
        </p>

        <div className="mt-8 grid gap-3">
          <Link
            to="/login"
            className="bg-[#0A1128] text-[#F9F7F4] px-5 py-4 inline-flex items-center justify-between rounded-2xl shadow-md"
            data-testid="account-signin-cta"
          >
            <span className="font-semibold tracking-wide">Sign in</span>
            <ArrowRight size={16} className="text-[#ed6500]" />
          </Link>
          <Link
            to="/register"
            className="bg-white border hairline px-5 py-4 inline-flex items-center justify-between text-[#0A1128] hover:border-[#ed6500] transition-colors rounded-2xl"
            data-testid="account-register-cta"
          >
            <span className="font-semibold tracking-wide">Create account</span>
            <ArrowRight size={16} className="text-[#ed6500]" />
          </Link>
        </div>

        <p className="mt-8 text-xs text-[#515E7B]">
          New to Link? <Link to="/about" className="text-[#ed6500] hover:underline">Learn how it works</Link>.
        </p>
        </div>
      </div>
    </div>
  );
}
